<template>
  <section class="contact-form-widget grid">
    <div class="container">
      <WidgetHeading class="t-h1" :is-first="isFirst" :content="widget.title" />
      <Form
        class="form"
        :form-fields="formFields"
        :form-error="formError"
        :is-loading="isLoading"
        hide-rule-text
        button-color="primary"
        :button-text="t('form.submit')"
        @submit="submit"
      />
    </div>
    <BasePopup :active="popupVisibility" @close="closePopup">
      <ThanksPopupContent :thanks="t('contact-thanks')" />
    </BasePopup>
  </section>
</template>

<script lang="ts" setup>
import type { PublicContactFormWidget } from '@/service/__generated-api'
import type { EndpointQuery, Widget } from '@/types/utils'

const { t } = useI18n()

defineProps<Widget<PublicContactFormWidget>>()

const {
  visibility: popupVisibility,
  open: openPopup,
  close: closePopup
} = usePopup()

const { formError, setFormError } = useFormNonFieldError(
  t('form.something-went-wrong-try-again')
)
const isLoading = ref(false)

const formFields = computed(() => [
  {
    label: t('form.first-name.label'),
    name: 'first_name',
    rules: 'required',
    placeholder: t('form.first-name.placeholder')
  },
  {
    label: t('form.email.label'),
    name: 'email',
    rules: 'email|required',
    placeholder: t('form.email.placeholder')
  },
  {
    label: t('form.message.label'),
    name: 'text',
    rules: 'required|max-length-255',
    placeholder: t('form.message.placeholder'),
    type: 'textarea'
  }
])

const submit = async (
  formData: EndpointQuery<'publicContactMessageCreate'>
) => {
  try {
    isLoading.value = true
    await useApi().publicContactMessageCreate(formData)
    openPopup()
  } catch (err) {
    setFormError(err)
  } finally {
    isLoading.value = false
  }
}
</script>

<style lang="scss" scoped>
.container {
  @include column(xs, 12);
  @include flex-col;
  gap: rem(40px);
}

.form {
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;

  :deep(> *) {
    width: 100%;
  }

  :deep(.field) {
    $base-margin: rem(18px);

    &:first-child {
      margin-bottom: calc(#{rem(28px)} - #{$base-margin});

      @include media-up(md) {
        margin-bottom: 0;
      }
    }

    &:first-child,
    &:nth-child(2) {
      @include media-up(md) {
        width: calc(50% - (#{rem(48px)} / 2));
      }

      @include media-up(s-lg) {
        width: calc(50% - (#{rem(80px)} / 2));
      }
    }

    &:nth-child(2) {
      margin-bottom: calc(#{rem(54px)} - #{$base-margin});
    }

    &:nth-child(3) {
      margin-bottom: calc(#{rem(42px)} - #{$base-margin});
    }
  }

  :deep(.button) {
    margin-inline: auto;
  }
}
</style>
